/* eslint-disable tailwindcss/no-custom-classname, tailwindcss/no-arbitrary-value */
'use client';

import { ApplyCtaLink } from '../../components/ApplyCta';
import Button from '../../components/Button';
import { CmsImage } from '../../components/CmsImage';
import { MaterialIcon } from '../../components/MaterialIcon';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { getApplyButtonLabel } from '../../utils/helpers/router';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { tr } from '../../utils/translations/tr';
import {
  buildSummaryRows,
  type Content,
  formatDiscountHighlight,
  formatSummaryPrice,
} from './format';
import { CONTENT_TRANSLATIONS } from './i18n';
import { COMPONENT_TYPE, type PlanTierApplyTracking } from './tracking';
import type { PlanTierSummarySource } from './types';

type PlanTierSummaryPanelProps = {
  /** Raw source data; omitted / `undefined` renders the skeleton (CMS-1864 Q1). */
  source?: PlanTierSummarySource;
  /** Active trip type — drives the cover-type row copy. */
  isAnnual: boolean;
  /** ISO currency code shown with the price, e.g. "SGD". */
  currencyCode: string;
  /** Apply-CTA click tracking, shared with the tier cards (CMS-1795). */
  trackApply: PlanTierApplyTracking;
};

/**
 * Desktop-only product summary panel (CMS-1864, Figma 27410:57429) rendered to
 * the right of the tier carousel. Mirrors the selected tier: full product name,
 * provider logo, price, trip-detail rows and the Apply CTA. Resolves its own
 * copy and formats every value from the block's i18n (`useCmsClientContext`),
 * so the wrapper passes raw data — not display strings.
 */
export const PlanTierSummaryPanel = ({
  source,
  isAnnual,
  currencyCode,
  trackApply,
}: PlanTierSummaryPanelProps): JSX.Element => {
  const context = useCmsClientContext();
  const content = getClientTranslations(CONTENT_TRANSLATIONS, context);

  return (
    <aside
      data-component="plan-tier-summary-panel"
      className="flex flex-col gap-spacing/gap/y rounded-radius/card border border-borders/default bg-fill/card px-spacing/padding/x py-spacing/padding/y shadow-block-interactive"
    >
      {source ? (
        <PanelContent
          source={source}
          content={content}
          isAnnual={isAnnual}
          currencyCode={currencyCode}
          brandCurrency={context.currencyDisplay}
          lang={context.lang}
          trackApply={trackApply}
        />
      ) : (
        <PanelSkeleton />
      )}
    </aside>
  );
};

const PanelContent = ({
  source,
  content,
  isAnnual,
  currencyCode,
  brandCurrency,
  lang,
  trackApply,
}: {
  source: PlanTierSummarySource;
  content: Content;
  isAnnual: boolean;
  currencyCode: string;
  brandCurrency: string | undefined;
  lang: string;
  trackApply: PlanTierApplyTracking;
}): JSX.Element => {
  const highlightText = formatDiscountHighlight(
    source.discountPercent,
    source.brandName,
    content,
  );
  const price = source.price
    ? formatSummaryPrice(source.price, currencyCode, brandCurrency)
    : undefined;
  const rows = buildSummaryRows(source.trip, isAnnual, content, lang);
  const ctaLabel = getApplyButtonLabel(
    tr('applyNow', content),
    undefined,
    source.product.call_to_action,
  );

  return (
    <>
      {/* Title + discount highlight subgroup (tighter gap than the panel gap). */}
      <div className="flex flex-col gap-spacing/gap/y-sm">
        <h3 className="font-bold text-text/primary text-font-size/display-1 leading-line-height/display-1">
          {source.title}
        </h3>

        {highlightText && (
          <p
            data-component="plan-tier-summary-highlight"
            className="rounded-radius/button bg-highlights/green-light px-tag/padding-x font-medium text-highlights/green-dark text-font-size/tag leading-line-height/tag"
          >
            {highlightText}
          </p>
        )}
      </div>

      <div className="flex items-start gap-spacing/gap/x">
        <span className="flex w-30 shrink-0 items-center justify-center overflow-hidden rounded-radius/card-inner border border-borders/default px-spacing/padding/x py-spacing/padding/y">
          {source.providerLogoUrl ? (
            <CmsImage
              src={source.providerLogoUrl}
              alt={source.providerName}
              width={192}
              height={48}
              sizes={{ _: 96 }}
              className="h-12 max-h-12 w-full object-contain"
            />
          ) : (
            // Logo missing — fall back to the provider name as text.
            <span className="text-center font-semibold text-text/primary text-font-size/caption leading-line-height/caption">
              {source.providerName}
            </span>
          )}
        </span>

        <span className="flex min-w-0 flex-col justify-center gap-spacing/gap/y-xs">
          {price ? (
            <>
              <span className="font-bold text-text/primary text-font-size/display-1 leading-line-height/display-1">
                {price.offered}
              </span>
              {price.original && (
                <span
                  data-component="plan-tier-summary-original-price"
                  className="font-medium text-text/secondary-lighter line-through text-[16px] leading-[22px]"
                >
                  {price.original}
                </span>
              )}
            </>
          ) : (
            source.isPriceLoading && <PricePulse />
          )}
        </span>
      </div>

      {rows.length > 0 && (
        <dl className="flex flex-col gap-spacing/gap/y-sm">
          {rows.map((row) => (
            <div
              key={row.id}
              data-component="plan-tier-summary-row"
              className="flex items-center gap-spacing/gap/x"
            >
              <dt className="flex w-30 shrink-0 items-center gap-spacing/gap/x-xs font-medium text-text/secondary-lighter text-font-size/body-2 leading-line-height/body-2">
                <MaterialIcon icon={row.icon} px={18} />
                {row.label}
              </dt>
              <dd className="min-w-0 flex-1 font-semibold text-text/primary text-font-size/bullet-2 leading-line-height/bullet-2">
                {row.value}
              </dd>
            </div>
          ))}
        </dl>
      )}

      <ApplyCtaLink
        product={source.product}
        target="_blank"
        rel="noopener noreferrer"
        className="w-full"
      >
        <Button
          styleType="cta"
          variant="filled"
          size="small"
          className="w-full justify-center rounded-[24px]"
          onClick={() =>
            trackApply({
              product: source.product,
              index: source.index,
              price: source.price,
              componentType: COMPONENT_TYPE.summaryPanel,
            })
          }
        >
          <span className="min-w-0 truncate text-sm">{ctaLabel}</span>
        </Button>
      </ApplyCtaLink>
    </>
  );
};

// Pulsing bars standing in for the price while the premium resolves — same
// technique as the tier card's PriceSkeleton (1lh keeps the resolved height).
const PricePulse = (): JSX.Element => (
  <span
    aria-hidden
    data-component="plan-tier-summary-price-pulse"
    className="flex flex-col gap-spacing/gap/y-xs"
  >
    <span className="flex h-[1lh] items-center text-font-size/display-1 leading-line-height/display-1">
      <span className="h-[22px] w-28 animate-pulse rounded-radius/button bg-fill/alternative-3" />
    </span>
    <span className="flex h-[1lh] items-center text-[16px] leading-[22px]">
      <span className="h-3 w-20 animate-pulse rounded-radius/button bg-fill/alternative-3" />
    </span>
  </span>
);

// Whole-panel skeleton shown until the user selects a tier (CMS-1864 Q1
// default): title, logo + price, four rows and the CTA slot all pulse.
const PanelSkeleton = (): JSX.Element => (
  <span
    data-component="plan-tier-summary-skeleton"
    aria-hidden
    className="flex flex-col gap-spacing/gap/y"
  >
    <span className="h-6 w-4/5 animate-pulse rounded-radius/button bg-fill/alternative-3" />
    <span className="flex items-start gap-spacing/gap/x">
      <span className="h-20 w-30 shrink-0 animate-pulse rounded-radius/card-inner bg-fill/alternative-3" />
      <PricePulse />
    </span>
    <span className="flex flex-col gap-spacing/gap/y-sm">
      {['row-1', 'row-2', 'row-3', 'row-4'].map((rowKey) => (
        <span
          key={rowKey}
          className="h-4 w-full animate-pulse rounded-radius/button bg-fill/alternative-3"
        />
      ))}
    </span>
    <span className="h-9 w-full animate-pulse rounded-[24px] bg-fill/alternative-3" />
  </span>
);
